import * as lodash from 'lodash';

import { environment } from '@environments/environment';

import { QueryBuilder } from '@cxc/core/builders';

const API = environment.PORTAL_API;
const FACILITIES_API = environment.FACILITIES_API;
const COMPANIES_API = environment.COMPANIES_API;
const trusts = FACILITIES_API + '/api/v1/trusts';
const identity = API + '/api/v1/identity';
const notifications = API + '/api/v1/notify';
const companies = COMPANIES_API + '/api/v1/company';

export const platformAPI = {
  identity: {
    users: {
      me: identity + '/users/me',
      update: '',
    },

    auth: {
      signup: identity + '/auth/signup',
      login: identity + '/auth/login',
      verifyTwoFA: identity + '/auth/verify-2fa',
      forgotPassword: identity + '/auth/forgot-password',
      confirmForgotPassword: identity + '/auth/confirm-forgot-password',
      logout: identity + '/auth/logout',
      refreshToken: identity + '/auth/refresh-token',
    },

    invitations: lodash.merge(identity + '/invitations', {
      invitationCode: (invitationCode: string) =>
        identity + `/invitations/${invitationCode}`,
    }),
  },

  notify: {
    notifications: notifications + '/notifications',
    unreadCount: notifications + '/notifications/unread-count',
  },

  trusts: (trustId?: string) => {
    let _trustId = '';

    if (trustId) {
      _trustId = trustId.replace('/', '-');
    }

    return {
      operations: lodash.merge(`${trusts}/operations`, {
        operations: (query: QueryBuilder) =>
          `${trusts}/operations${query.build()}`,
        directory: `${trusts}/operations/${_trustId}/directory`,
        overview: `${trusts}/operations/${_trustId}/overview`,
        filters: `${trusts}/operations/filters`,
        contracts: (query: QueryBuilder) =>
          `${trusts}/operations/${_trustId}/contracts${query.build()}`,
        reports: (query: QueryBuilder) =>
          `${trusts}/operations/${_trustId}/reports${query.build()}`,
        insights: (query: QueryBuilder) =>
          `${trusts}/operations/${_trustId}/insights${query.build()}`,
        insightsResume: `${trusts}/operations/${_trustId}/insights-resume`,
        cashFlowResume: `${trusts}/operations/${_trustId}/cash-flow-resume`,
        matrix: (query: QueryBuilder) =>
          `${trusts}/operations/${_trustId}/matrix${query.build()}`,
        allCharts: `${trusts}/operations/charts`,
        chartsByOperation: `${trusts}/operations/${_trustId}/charts`,
        chartsV2ByOperation: `${trusts}/operations/${_trustId}/charts/v2`,
      }),

      system: {
        currencies: `${trusts}/system/currencies`,
        currencyRate: `${trusts}/system/currency/rate`,
        operations: {
          lastReport: () =>
            `${trusts}/system/operations/${_trustId}/reports/last_report`,
        },
      },

      lender: {
        coreMetrics: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/core-metrics${query.build()}`,
        // LEN-2948: the cut-off dates on their own. `core-metrics` answered
        // them too, but it also serves the Overview's figures, so reading a
        // date meant holding a permission that came with the money attached.
        cutOffDates: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/cut-off-dates${query.build()}`,
        overview: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/overview${query.build()}`,
        overviewResume: `${trusts}/lender/${_trustId}/overview-resume`,
        matrix: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/matrix${query.build()}`,
        charts: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/charts${query.build()}`,
        operations: (query: QueryBuilder) =>
          `${trusts}/lender/${_trustId}/operations${query.build()}`,
      },
      storage: lodash.merge(`${trusts}/storage`, {
        id: (trustID: string) => {
          return {
            download: `${trusts}/storage/${trustID}/download`,
            downloadMultiple: `${trusts}/storage/${trustID}/download-multiple`,
          };
        },
      }),
    };
  },

  companies: {
    list: `${companies}/companies`,
    catalogs: {
      assetTypes: `${companies}/catalogs/asset_types`,
      operationTypes: `${companies}/catalogs/operation_types`,
    },
    facilities: `${companies}/facilities`,
    reports: lodash.merge(`${companies}/reports`, {
      filesMultiple: `${companies}/reports/files/multiple`,
      filesDownload: `${companies}/reports/files/download`,
      uploadUrl: `${companies}/reports/upload-url`,
      complete: `${companies}/reports/complete`,
    }),
    contracts: lodash.merge(`${companies}/contracts`, {
      search: `${companies}/contracts/search`,
      byId: (contractId: string) => `${companies}/contracts/${contractId}`,
    }),
  },
};
